import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './AppointmentFlows.styled';
import AppointmentAnalysesList from 'components/Analyses/AppointmentAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useAppointmentFlows from 'hooks/useAppointmentFlows';
import useAppointmentAnalyses from 'hooks/useAppointmentAnalyses';
import { DELETE_APPOINTMENT_FLOW } from 'helpers/confirmations';
import { checkEmptyString, needToUpdateField } from 'helpers';
import { appointmentFlowsSelectors } from "features/appointmentFlows";
import { profileSelectors } from 'features/profile';

const AppointmentFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getAppointmentFlow, createAppointmentFlow, updateAppointmentFlow } = useAppointmentFlows();
  const { getAppointmentAnalyses, clearLocalAppointmentAnalyses } = useAppointmentAnalyses();
  const {  email: myEmail } = useCustomSelector(profileSelectors.getProfileData);
  const id = useCustomSelector(appointmentFlowsSelectors.getAppointmentFlowId);
  const email = useCustomSelector(appointmentFlowsSelectors.getAppointmentFlowEmail);
  const appointmentFlow = useCustomSelector(appointmentFlowsSelectors.getAppointmentFlowData);
  const fetching = useCustomSelector(appointmentFlowsSelectors.appointmentFlowIsFetching);
  const [mailPrompt, setMailPrompt] = useState({ value: '', error: '' });
  const [appointmentPrompt, setAppointmentPrompt] = useState({ value: '', error: '' });
  const [patientPrompt, setPatientPrompt] = useState({ value: '', error: '' });
  const [cliniciansPrompt, setCliniciansPrompt] = useState({ value: '', error: '' });
  const [slotsPrompt, setSlotsPrompt] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (appointment_flow) => {
    setMailPrompt({ value: appointment_flow?.mail_prompt ?? '', error: '' });
    setAppointmentPrompt({ value: appointment_flow?.appointment_prompt ?? '', error: '' });
    setPatientPrompt({ value: appointment_flow?.patient_prompt ?? '', error: '' });
    setCliniciansPrompt({ value: appointment_flow?.clinicians_prompt ?? '', error: '' });
    setSlotsPrompt({ value: appointment_flow?.slots_prompt ?? '', error: '' });
  };
  const handleExited = () => {
    setMailPrompt({ value: '', error: '' });
    setAppointmentPrompt({ value: '', error: '' });
    setPatientPrompt({ value: '', error: '' });
    setCliniciansPrompt({ value: '', error: '' });
    setSlotsPrompt({ value: '', error: '' });
    
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(cliniciansPrompt.value)) {
      setCliniciansPrompt(prev => ({ ...prev, error: `Clinicians Prompt can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(slotsPrompt.value)) {
      setSlotsPrompt(prev => ({ ...prev, error: `Slots Prompt can't be empty` }) );
      hasError = true;
    }

    if (hasError) return;

    const appointment_flow = {
      ...(needToUpdateField(appointmentFlow?.mail_prompt, mailPrompt.value) && { mail_prompt: mailPrompt.value }),
      ...(needToUpdateField(appointmentFlow?.appointment_prompt, appointmentPrompt.value) && { appointment_prompt: appointmentPrompt.value }),
      ...(needToUpdateField(appointmentFlow?.patient_prompt, patientPrompt.value) && { patient_prompt: patientPrompt.value }),
      ...(needToUpdateField(appointmentFlow?.clinicians_prompt, cliniciansPrompt.value) && { clinicians_prompt: cliniciansPrompt.value }),
      ...(needToUpdateField(appointmentFlow?.slots_prompt, slotsPrompt.value) && { slots_prompt: slotsPrompt.value })
    };

    if (!_isEmpty(appointment_flow)) {
      if (!!id) {
        updateAppointmentFlow(appointment_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createAppointmentFlow(appointment_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_APPOINTMENT_FLOW });
  const handleCopy = () => {
    if (!!email && !copied)
    {
      navigator.clipboard.writeText(email);
      setCopied(true);
    }
  };
  const fetchAppointmentAnalyses = useCallback(() => {
    getAppointmentAnalyses({ offset: 0 })
  }, [getAppointmentAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalAppointmentAnalyses();
    };
  }, [id, clearLocalAppointmentAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getAppointmentFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { appointment_flow } = payload;
          handleEntering(appointment_flow);
          fetchAppointmentAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getAppointmentFlow, fetchAppointmentAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Tooltip 
              title={!!email ? (copied ? 'Copied!' : 'Click to copy') : ''}
              followCursor
              onClose={() => setTimeout(() => setCopied(false), 300)} 
            >
              <Typography 
                variant='subtitle2'
                component='div' 
                sx={{ wordBreak: 'break-all', cursor: 'pointer' }}
                onClick={handleCopy}
              >
                {`Receiving Email: ${!!email ? email : '-'}`}
              </Typography>
            </Tooltip>
            <Typography variant='subtitle2' component='div' sx={{ wordBreak: 'break-all' }}>{`Email: ${!!myEmail ? myEmail : '-'}`}</Typography>
            <TextField
              fullWidth
              label='Mail Prompt'
              multiline
              sx={{ mt: '5px' }}
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={mailPrompt.error}
              error={!!mailPrompt.error}
              value={mailPrompt.value}
              onChange={({ target: { value } }) => setMailPrompt({ value, error: '' })}
            />
            <TextField
              fullWidth
              label='Appointment Prompt'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={appointmentPrompt.error}
              error={!!appointmentPrompt.error}
              value={appointmentPrompt.value}
              onChange={({ target: { value } }) => setAppointmentPrompt({ value, error: '' })}
            />
            <TextField
              fullWidth
              label='Patient Prompt'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={patientPrompt.error}
              error={!!patientPrompt.error}
              value={patientPrompt.value}
              onChange={({ target: { value } }) => setPatientPrompt({ value, error: '' })}
            />
            <Accordion sx={{ width: '100%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3-content"
                id="panel3-header"
              >
                <Typography>Variables for clinicians prompt</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap', 
                    wordWrap: 'break-word', 
                  }}
                >
                  {`"insurance_type"\n"insurance_carrier"\n"insurance_number"\n"appointment_type"\n"diagnosis"\n"preferred_clinician"`}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <TextField
              fullWidth
              label='Clinicians Prompt'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={cliniciansPrompt.error}
              error={!!cliniciansPrompt.error}
              value={cliniciansPrompt.value}
              onChange={({ target: { value } }) => setCliniciansPrompt({ value, error: '' })}
            />
            <Accordion sx={{ width: '100%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel4-content"
                id="panel4-header"
              >
                <Typography>Variables for slots prompt</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap', 
                    wordWrap: 'break-word', 
                  }}
                >
                  {`"insurance_type"\n"insurance_carrier"\n"insurance_number"\n"appointment_type"\n"diagnosis"\n"preferred_clinician"`}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <TextField
              fullWidth
              label='Slots Prompt'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={slotsPrompt.error}
              error={!!slotsPrompt.error}
              value={slotsPrompt.value}
              onChange={({ target: { value } }) => setSlotsPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <AppointmentAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <AppointmentFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);